import React, { useCallback, useMemo, useState } from 'react';

import { difference, get, isEmpty, xor } from 'lodash';
import { FormattedMessage } from 'react-intl';

import type {
  GroupedPendingItem,
  PendingItem,
  TableClickActions,
} from '@modules/advancement/pendingItems/types';
import { searchOnItems } from '@modules/advancement/purchaseOrder/utils';
import { ApproveAdvancementsButton } from '@modules/progress/approveAdvancements';
import { Fab, TableMobile } from '@modules/shared';

import styles from './PendingItemsList.less';
import type { FlatListItem } from './PendingRequirementsList';
import { rednerHeaderRow } from './PendingRequirementsListHeaderRow';
import { renderDefaultRows } from './PendingRequirementsListRows';

type SelectedItemKey = string | number;

interface PendingRequirementsListNotApprovable {
  onGoToYouthProfile: TableClickActions['onGoToYouthProfile'];
  searchQuery: string;
  notApprovableItemsWithChildren: GroupedPendingItem[];
  isLoading: boolean;
  isOffline: boolean;
}

const PendingRequirementsListNotApprovable: React.FC<
  PendingRequirementsListNotApprovable
> = props => {
  const {
    searchQuery,
    notApprovableItemsWithChildren: dataSource,
    isLoading,
    onGoToYouthProfile,
    isOffline,
  } = props;
  const [selectedRowKeys, setSelectedRowKeys] = useState<SelectedItemKey[]>([]);
  const searchedItemsNotApprovable = searchOnItems(searchQuery, dataSource);
  const selectedAdvancements = useMemo(() => {
    const setKeys = new Set(selectedRowKeys);

    return dataSource
      .flatMap(({ children }) => children)
      .filter(item => setKeys.has(item.key))
      .filter(Boolean);
  }, [dataSource, selectedRowKeys]);

  const onSelectGroup = useCallback(
    (groupKey: string) => {
      const group = searchedItemsNotApprovable.find(
        ({ key }) => key === groupKey,
      );
      const groupItems = get(group, 'children', []).map(
        ({ key }: { key: string | number }) => key,
      );
      const selectedInGroup = selectedRowKeys.filter(item =>
        groupItems?.includes(item),
      );
      const diff = difference(groupItems, selectedRowKeys);

      if (groupItems?.length === selectedInGroup.length) {
        return setSelectedRowKeys(xor(selectedRowKeys, groupItems));
      }

      return setSelectedRowKeys(xor(selectedRowKeys, diff));
    },
    [searchedItemsNotApprovable, selectedRowKeys],
  );

  const onSelectItem = useCallback(
    (record: GroupedPendingItem | PendingItem) => {
      const selectedItems = [record.key];

      setSelectedRowKeys(xor(selectedRowKeys, selectedItems));
    },
    [selectedRowKeys],
  );

  const flatItems = useMemo(
    () =>
      searchedItemsNotApprovable.reduce<FlatListItem[]>(
        (result, item: GroupedPendingItem) => {
          result.push({
            row: rednerHeaderRow({
              isOffline,
              item,
              onGoToYouthProfile,
              onSelectGroup,
              selectedRowKeys,
            }),
            key: item.key,
            type: 'custom',
          });

          item.children.forEach(row => {
            result.push(row);
          });

          return result;
        },
        [],
      ),
    [
      isOffline,
      searchedItemsNotApprovable,
      onGoToYouthProfile,
      onSelectGroup,
      selectedRowKeys,
    ],
  );

  return (
    <>
      <p className={styles.disclaimer}>
        <FormattedMessage id="advancement.PendingItems.nonApprovable.disclaimer" />
      </p>
      <TableMobile
        items={flatItems}
        loading={isLoading}
        rowId="key"
        rows={renderDefaultRows()}
        selectedKeys={selectedRowKeys}
        onItemClick={onSelectItem}
      />
      <Fab
        visible={!isOffline && !isEmpty(selectedRowKeys)}
        className="Joyride__ReportFab"
        inline
      >
        <ApproveAdvancementsButton
          advancementStatus={null}
          className={styles.approveAdvancements}
          personsAdvancements={selectedAdvancements}
        />
      </Fab>
    </>
  );
};

export default PendingRequirementsListNotApprovable;
